import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Form, Modal, Spinner } from "react-bootstrap";
import { AppDispatch, RootState } from "../../store";
import { fetchBmsCalendar } from "../../features/bms/bmsSlice";
import {
  TbCalendar,
  TbChevronLeft,
  TbChevronRight,
  TbSun,
  TbCalendarEvent,
  TbTimeline,
  TbX,
  TbUser,
  TbPhone,
  TbMail,
  TbClock,
  TbBriefcase,
  TbPlus,
  TbEdit,
  TbCheck,
  TbSearch,
  TbUserPlus,
} from "react-icons/tb";
import BmsChatbotSelector from "../../components/bms/BmsChatbotSelector";
import { showError, showSuccess } from "../../utils/bmsToast";
import bmsService, { BmsCalendarResource, BmsCalendarEvent, BmsCustomer, BmsServiceItem } from "../../features/bms/bmsService";
import CreateBookingFromCalendarModal, { CalendarBookingContext } from "../../components/bms/CreateBookingFromCalendarModal";

type ViewMode = "day" | "week" | "timeline";

const EXPERT_COLORS = [
  "#521DB3", "#ec4899", "#14b8a6", "#f59e0b", "#8b5cf6",
  "#06b6d4", "#ef4444", "#22c55e", "#e11d48", "#6366f1",
];

const DAY_NAMES_SHORT = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];
const MONTH_NAMES = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];
const DAY_NAMES_FULL = [
  "Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi",
];

function hexToRgba(hex: string, a: number) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
}

function extractTime(dateStr: string): string {
  if (!dateStr) return "";
  const tIdx = dateStr.indexOf("T");
  const spIdx = dateStr.indexOf(" ");
  const sep = tIdx >= 0 ? tIdx : spIdx >= 0 ? spIdx : -1;
  if (sep < 0) return "";
  return dateStr.slice(sep + 1, sep + 6);
}

function parseHour(dateStr: string): number {
  const t = extractTime(dateStr);
  if (!t) return 0;
  const [hh, mm] = t.split(":").map(Number);
  return (hh || 0) + (mm || 0) / 60;
}

function formatDate(date: Date): string {
  return `${date.getDate()} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}, ${DAY_NAMES_FULL[date.getDay()]}`;
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getWeekStart(d: Date): Date {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(d, diff);
}

function parseTimeField(val: string | null | undefined): number | null {
  if (!val) return null;
  const parts = val.split(":").map(Number);
  if (parts.length < 2) return null;
  return parts[0] + parts[1] / 60;
}

function getEventsForDate(events: BmsCalendarEvent[], date: Date): BmsCalendarEvent[] {
  const ds = toIsoDate(date);
  return events.filter((ev) => {
    const s = ev.start_at || "";
    return s.startsWith(ds + "T") || s.startsWith(ds + " ") || s.slice(0, 10) === ds;
  });
}

interface ExpertInfo {
  id: number;
  name: string;
  color: string;
  initials: string;
}

interface ModalData {
  ev: BmsCalendarEvent;
  expertName: string;
  color: string;
}

// Fallback window when nobody has availability configured
const DEFAULT_START_HOUR = 8;
const DEFAULT_END_HOUR = 20;
const PX_PER_HOUR = 80;

// Compute union of working windows across resources that apply to any of the
// given weekdays. Returns inclusive start hour and exclusive end hour, rounded
// to whole hours, with a small padding so events on the edges are not clipped.
function computeVisibleHours(
  resources: BmsCalendarResource[],
  weekdays: number[]
): { startHour: number; endHour: number } {
  let minStart: number | null = null;
  let maxEnd: number | null = null;
  for (const r of resources) {
    for (const a of r.availability || []) {
      const days = Array.isArray(a.days) ? a.days : [];
      if (!weekdays.some((w) => days.includes(w))) continue;
      const s = parseTimeField(a.start_at);
      const e = parseTimeField(a.end_at);
      if (s !== null) minStart = minStart === null ? s : Math.min(minStart, s);
      if (e !== null) maxEnd = maxEnd === null ? e : Math.max(maxEnd, e);
    }
  }
  if (minStart === null || maxEnd === null || maxEnd <= minStart) {
    return { startHour: DEFAULT_START_HOUR, endHour: DEFAULT_END_HOUR };
  }
  // Round down start, round up end; add ±1h padding, clamp to 0..24
  let startHour = Math.max(0, Math.floor(minStart) - 1);
  let endHour = Math.min(24, Math.ceil(maxEnd) + 1);
  // Ensure a sensible minimum span
  if (endHour - startHour < 6) {
    const mid = (startHour + endHour) / 2;
    startHour = Math.max(0, Math.floor(mid - 3));
    endHour = Math.min(24, startHour + 6);
  }
  return { startHour, endHour };
}

function buildHourList(startHour: number, endHour: number): number[] {
  const out: number[] = [];
  for (let h = startHour; h < endHour; h++) out.push(h);
  return out;
}

const statusInfo = (s: string, t: (k: string) => string) => {
  switch (s) {
    case "confirmed": return { cls: "confirmed", label: t("bms_confirmed") };
    case "pending": return { cls: "pending", label: t("bms_pending") };
    case "cancelled": return { cls: "cancelled", label: t("bms_cancelled") };
    case "completed": return { cls: "completed", label: t("bms_completed") };
    default: return { cls: "", label: s };
  }
};

/* ═══════════════════ DETAIL DRAWER ═══════════════════ */

const STATUS_OPTIONS = ["confirmed", "pending", "cancelled", "completed"] as const;

const BookingDetailModal: React.FC<{
  data: ModalData | null;
  onClose: () => void;
  chatbotId: number | null;
  onUpdated: () => void;
  t: (k: string) => string;
}> = ({ data, onClose, chatbotId, onUpdated, t }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editStatus, setEditStatus] = useState("");
  const [editStartAt, setEditStartAt] = useState("");
  const [editEndAt, setEditEndAt] = useState("");
  const [editCustName, setEditCustName] = useState("");
  const [editCustSurname, setEditCustSurname] = useState("");
  const [editCustPhone, setEditCustPhone] = useState("");
  const [editCustEmail, setEditCustEmail] = useState("");
  const [customers, setCustomers] = useState<BmsCustomer[]>([]);
  const [services, setServices] = useState<BmsServiceItem[]>([]);
  const [editCustomerId, setEditCustomerId] = useState<number>(0);
  const [editServiceId, setEditServiceId] = useState<number>(0);
  const [editIsNewCustomer, setEditIsNewCustomer] = useState(false);
  const [editCustomerSearch, setEditCustomerSearch] = useState("");
  const [editShowDropdown, setEditShowDropdown] = useState(false);
  const editSearchRef = useRef<HTMLDivElement>(null);
  const [editServiceSearch, setEditServiceSearch] = useState("");
  const [editShowServiceDropdown, setEditShowServiceDropdown] = useState(false);
  const editServiceSearchRef = useRef<HTMLDivElement>(null);

  // Reset edit state when drawer opens with new data
  useEffect(() => {
    if (data) {
      setEditing(true);
      setEditStatus(data.ev.status);
      const toLocal = (s: string) => (s || "").replace(" ", "T").slice(0, 16);
      setEditStartAt(toLocal(data.ev.start_at));
      setEditEndAt(toLocal(data.ev.end_at));
      setEditCustName(data.ev.customer_name || "");
      setEditCustSurname("");
      setEditCustPhone(data.ev.customer_phone || "");
      setEditCustEmail(data.ev.customer_email || "");
      const matched = customers.find((c) => {
        const samePhone = !!data.ev.customer_phone && c.phone === data.ev.customer_phone;
        const sameName = c.name.trim().toLowerCase() === (data.ev.customer_name || "").trim().toLowerCase();
        return samePhone || sameName;
      });
      const matchedService = services.find((s) => s.name.trim().toLowerCase() === (data.ev.service_name || "").trim().toLowerCase());
      setEditCustomerId(matched?.id || 0);
      setEditServiceId(matchedService?.id || 0);
      setEditIsNewCustomer(false);
      setEditCustomerSearch("");
      setEditShowDropdown(false);
      setEditServiceSearch("");
      setEditShowServiceDropdown(false);
    }
  }, [data, customers, services]);

  useEffect(() => {
    const loadRefs = async () => {
      if (!data || !chatbotId) return;
      try {
        const [cRes, sRes] = await Promise.all([
          bmsService.listCustomers(chatbotId, { limit: 200 }),
          bmsService.listServices(chatbotId, { limit: 200 }),
        ]);
        setCustomers(cRes.data || []);
        setServices((sRes.data || []).filter((s) => !!s.is_active));
      } catch {
        setCustomers([]);
        setServices([]);
      }
    };
    loadRefs();
  }, [data, chatbotId]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (editSearchRef.current && !editSearchRef.current.contains(e.target as Node)) {
        setEditShowDropdown(false);
      }
      if (editServiceSearchRef.current && !editServiceSearchRef.current.contains(e.target as Node)) {
        setEditShowServiceDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!data) return null;
  const { ev, expertName, color } = data;
  const si = statusInfo(ev.status, t);
  const startTime = extractTime(ev.start_at || "");
  const endTime = extractTime(ev.end_at || "");

  const handleSave = async () => {
    if (!chatbotId || !ev.id) return;
    if (!editServiceId) {
      showError(t("bms_select_service_booking"));
      return;
    }
    let resolvedCustomerId = editCustomerId;
    if (editIsNewCustomer) {
      const n = editCustName.trim().toLowerCase();
      const p = editCustPhone.trim();
      const existing = customers.find((c) => c.phone === p && c.name.trim().toLowerCase() === n);
      if (!existing) {
        showError(t("bms_invalid_customer"));
        return;
      }
      resolvedCustomerId = existing.id;
    }
    setSaving(true);
    try {
      await bmsService.updateBooking(chatbotId, ev.id, {
        status: editStatus,
        start_at: editStartAt.replace("T", " "),
        end_at: editEndAt.replace("T", " "),
        ...(editServiceId ? { service_id: editServiceId } : {}),
        ...(resolvedCustomerId ? { customer_id: resolvedCustomerId } : {}),
      });
      showSuccess(t("bms_booking_updated"));
      setEditing(false);
      onClose();
      onUpdated();
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={!!data} onHide={onClose} className="bms-modal" centered size="lg">
      <Modal.Header closeButton className="bms-modal-header">
        <Modal.Title className="d-flex align-items-center gap-2">
          <TbEdit /> {t("bms_edit_booking")}
          {ev.booking_number ? ` — ${ev.booking_number}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3">
          <div className="col-md-6">
            <Form.Label className="bms-form-label">{t("bms_status")}</Form.Label>
            <div className="bms-status-btn-group">
              {STATUS_OPTIONS.map((s) => {
                const info = statusInfo(s, t);
                return (
                  <button
                    key={s}
                    type="button"
                    className={`bms-status-btn ${info.cls}${editStatus === s ? " active" : ""}`}
                    onClick={() => setEditStatus(s)}
                  >
                    <span className="bms-status-dot" /> {info.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="col-md-6">
            <Form.Label className="bms-form-label">{t("bms_cal_expert")}</Form.Label>
            <Form.Control value={expertName} disabled />
          </div>
          <div className="col-md-6">
            <Form.Label className="bms-form-label">{t("bms_start")}</Form.Label>
            <Form.Control type="datetime-local" value={editStartAt} onChange={(e) => setEditStartAt(e.target.value)} />
          </div>
          <div className="col-md-6">
            <Form.Label className="bms-form-label">{t("bms_end")}</Form.Label>
            <Form.Control type="datetime-local" value={editEndAt} onChange={(e) => setEditEndAt(e.target.value)} />
          </div>
          <div className="col-md-6">
            <Form.Label className="bms-form-label">{t("bms_service")}</Form.Label>
            <div className="bms-customer-search-wrap" ref={editServiceSearchRef}>
              <div className="bms-customer-search-input">
                <TbSearch className="bms-cs-icon" />
                <input
                  type="text"
                  className="bms-cs-field"
                  placeholder={t("bms_select_service_booking")}
                  value={editServiceId ? (() => {
                    const s = services.find((x) => x.id === editServiceId);
                    return s ? `${s.name} (${s.duration_minutes} ${t("bms_minutes_short")})` : editServiceSearch;
                  })() : editServiceSearch}
                  onChange={(e) => {
                    setEditServiceSearch(e.target.value);
                    setEditServiceId(0);
                    setEditShowServiceDropdown(true);
                  }}
                  onFocus={() => setEditShowServiceDropdown(true)}
                />
                {editServiceId > 0 && (
                  <button
                    type="button"
                    className="bms-cs-clear"
                    onClick={() => {
                      setEditServiceId(0);
                      setEditServiceSearch("");
                      setEditShowServiceDropdown(true);
                    }}
                  >
                    <TbX size={14} />
                  </button>
                )}
              </div>
              {editShowServiceDropdown && !editServiceId && (
                <div className="bms-customer-dropdown">
                  {(() => {
                    const q = editServiceSearch.toLowerCase().trim();
                    const filtered = q ? services.filter((s) => s.name.toLowerCase().includes(q)) : services;
                    return filtered.length === 0 ? (
                      <div className="bms-cd-empty">{t("bms_no_data")}</div>
                    ) : (
                      filtered.slice(0, 8).map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          className="bms-cd-item"
                          onClick={() => {
                            setEditServiceId(s.id);
                            setEditServiceSearch("");
                            setEditShowServiceDropdown(false);
                          }}
                        >
                          <div className="bms-cd-info">
                            <div className="bms-cd-name">{s.name}</div>
                            <div className="bms-cd-meta">{s.duration_minutes} {t("bms_minutes_short")}</div>
                          </div>
                        </button>
                      ))
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <Form.Label className="bms-form-label">{t("bms_booking_number_label")}</Form.Label>
            <Form.Control value={ev.booking_number || "-"} disabled />
          </div>
          <div className="col-12">
            <div className="bms-customer-section">
              <div className="bms-customer-header">
                <Form.Label className="bms-form-label mb-0">{t("bms_customer_select_label")}</Form.Label>
                <label className="bms-new-customer-toggle">
                  <input
                    type="checkbox"
                    checked={editIsNewCustomer}
                    onChange={(e) => {
                      setEditIsNewCustomer(e.target.checked);
                      if (e.target.checked) {
                        setEditCustomerId(0);
                        setEditCustomerSearch("");
                        setEditShowDropdown(false);
                        setEditCustName("");
                        setEditCustSurname("");
                        setEditCustPhone("");
                        setEditCustEmail("");
                      } else {
                        setEditCustName("");
                        setEditCustSurname("");
                        setEditCustPhone("");
                        setEditCustEmail("");
                      }
                    }}
                  />
                  <span>{t("bms_new_customer")}</span>
                </label>
              </div>
              {!editIsNewCustomer ? (
                <div className="bms-customer-search-wrap" ref={editSearchRef}>
                  <div className="bms-customer-search-input">
                    <TbSearch className="bms-cs-icon" />
                    <input
                      type="text"
                      className="bms-cs-field"
                      placeholder={t("bms_customer_search_placeholder")}
                      value={editCustomerId ? (() => {
                        const c = customers.find((x) => x.id === editCustomerId);
                        return c ? `${c.name} ${c.surname} (${c.phone})` : editCustomerSearch;
                      })() : editCustomerSearch}
                      onChange={(e) => {
                        setEditCustomerSearch(e.target.value);
                        setEditCustomerId(0);
                        setEditShowDropdown(true);
                      }}
                      onFocus={() => setEditShowDropdown(true)}
                    />
                    {editCustomerId > 0 && (
                      <button
                        type="button"
                        className="bms-cs-clear"
                        onClick={() => {
                          setEditCustomerId(0);
                          setEditCustomerSearch("");
                          setEditShowDropdown(true);
                        }}
                      >
                        <TbX size={14} />
                      </button>
                    )}
                  </div>
                  {editShowDropdown && editCustomerId === 0 && (
                    <div className="bms-customer-dropdown">
                      {(() => {
                        const q = editCustomerSearch.toLowerCase().trim();
                        const filtered = q
                          ? customers.filter((c) =>
                              `${c.name} ${c.surname}`.toLowerCase().includes(q) ||
                              c.phone?.toLowerCase().includes(q) ||
                              c.email?.toLowerCase().includes(q)
                            )
                          : customers;
                        return filtered.length === 0 ? (
                          <div className="bms-cd-empty">{t("bms_customer_no_results")}</div>
                        ) : (
                          filtered.slice(0, 8).map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              className="bms-cd-item"
                              onClick={() => {
                                setEditCustomerId(c.id);
                                setEditCustomerSearch("");
                                setEditShowDropdown(false);
                              }}
                            >
                              <div className="bms-cd-avatar"><TbUser size={14} /></div>
                              <div className="bms-cd-info">
                                <div className="bms-cd-name">{c.name} {c.surname}</div>
                                <div className="bms-cd-meta">{c.phone}{c.email ? ` · ${c.email}` : ""}</div>
                              </div>
                            </button>
                          ))
                        );
                      })()}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bms-new-customer-form">
                  <div className="bms-ncf-hint"><TbUserPlus size={14} /> {t("bms_new_customer_hint")}</div>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <Form.Label className="bms-form-label">{t("bms_name")} *</Form.Label>
                      <Form.Control value={editCustName} onChange={(e) => setEditCustName(e.target.value)} />
                    </div>
                    <div className="col-md-6">
                      <Form.Label className="bms-form-label">{t("bms_surname")}</Form.Label>
                      <Form.Control value={editCustSurname} onChange={(e) => setEditCustSurname(e.target.value)} />
                    </div>
                    <div className="col-md-6">
                      <Form.Label className="bms-form-label">{t("bms_phone")} *</Form.Label>
                      <Form.Control value={editCustPhone} onChange={(e) => setEditCustPhone(e.target.value)} />
                    </div>
                    <div className="col-md-6">
                      <Form.Label className="bms-form-label">{t("bms_email")}</Form.Label>
                      <Form.Control type="email" value={editCustEmail} onChange={(e) => setEditCustEmail(e.target.value)} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="bms-modal-footer">
        <button className="bms-btn-secondary" onClick={onClose}>
          <TbX /> {t("cancel")}
        </button>
        <button className="bms-btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size="sm" animation="border" /> : <TbCheck />} {t("save")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

/* ═══════════════════ MAIN PAGE ═══════════════════ */

const BmsCalendarPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { calendarResources, isLoading, error } = useSelector((s: RootState) => s.bms);

  const [chatbotId, setChatbotId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("day");
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [activeExperts, setActiveExperts] = useState<Set<number>>(new Set());
  const [modalData, setModalData] = useState<ModalData | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createContext, setCreateContext] = useState<CalendarBookingContext | null>(null);

  const today = useMemo(() => new Date(), []);

  const expertInfos = useMemo<ExpertInfo[]>(() => {
    return calendarResources.map((r, idx) => {
      const name = r.resource.resource_name || "?";
      const words = name.split(" ").filter(Boolean);
      const initials = words.length >= 2
        ? (words[0][0] + words[words.length - 1][0]).toUpperCase()
        : name.slice(0, 2).toUpperCase();
      return {
        id: r.resource.resource_user_id,
        name,
        color: EXPERT_COLORS[idx % EXPERT_COLORS.length],
        initials,
      };
    });
  }, [calendarResources]);

  const visibleResources = useMemo(() => {
    if (activeExperts.size === 0) return calendarResources;
    return calendarResources.filter((r) => activeExperts.has(r.resource.resource_user_id));
  }, [calendarResources, activeExperts]);

  const visibleExperts = useMemo(() => {
    if (activeExperts.size === 0) return expertInfos;
    return expertInfos.filter((e) => activeExperts.has(e.id));
  }, [expertInfos, activeExperts]);

  const loadCalendar = useCallback(
    (date: Date, mode: ViewMode) => {
      if (!chatbotId) return;
      let startAt: string;
      let endAt: string;
      if (mode === "day") {
        startAt = toIsoDate(date) + "T00:00:00";
        endAt = toIsoDate(date) + "T23:59:59";
      } else if (mode === "week") {
        const ws = getWeekStart(date);
        startAt = toIsoDate(ws) + "T00:00:00";
        endAt = toIsoDate(addDays(ws, 6)) + "T23:59:59";
      } else {
        startAt = toIsoDate(date) + "T00:00:00";
        endAt = toIsoDate(date) + "T23:59:59";
      }
      dispatch(fetchBmsCalendar({ chatbotId, params: { start_at: startAt, end_at: endAt } }));
    },
    [chatbotId, dispatch]
  );

  useEffect(() => {
    if (chatbotId) {
      setActiveExperts(new Set());
      loadCalendar(currentDate, viewMode);
    }
  }, [chatbotId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (chatbotId) loadCalendar(currentDate, viewMode);
  }, [currentDate, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (error) showError(error);
  }, [error]);

  useEffect(() => {
    if (calendarResources.length > 0 && activeExperts.size === 0) {
      setActiveExperts(new Set(calendarResources.map((r) => r.resource.resource_user_id)));
    }
  }, [calendarResources]); // eslint-disable-line react-hooks/exhaustive-deps

  const navDate = (dir: number) => {
    const step = viewMode === "week" ? 7 : 1;
    setCurrentDate((d) => addDays(d, dir * step));
  };

  const goToday = () => setCurrentDate(new Date());

  const toggleExpert = (id: number) => {
    setActiveExperts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openModal = (ev: BmsCalendarEvent, expertName: string, color: string) => {
    setModalData({ ev, expertName, color });
  };

  const handleSlotClick = (date: Date, hour: number | undefined, expertId: number | undefined) => {
    setCreateContext({ date, hour, expertId });
    setShowCreateModal(true);
  };

  const handleBookingCreated = () => {
    loadCalendar(currentDate, viewMode);
  };

  const getUnavailableBlocks = (
    resource: BmsCalendarResource,
    date: Date,
    startHour: number,
    endHour: number
  ): Array<{ start: number; end: number; label: string }> => {
    const dayOfWeek = date.getDay();
    const blocks: Array<{ start: number; end: number; label: string }> = [];
    if (!resource.availability || resource.availability.length === 0) return blocks;
    let matched = false;
    for (const avail of resource.availability) {
      if (!avail.days.includes(dayOfWeek)) continue;
      matched = true;
      const workStart = parseTimeField(avail.start_at) ?? startHour;
      const workEnd = parseTimeField(avail.end_at) ?? endHour;
      if (workStart > startHour) blocks.push({ start: startHour, end: workStart, label: "" });
      if (workEnd < endHour) blocks.push({ start: workEnd, end: endHour, label: "" });
      const brkStart = parseTimeField(avail.break_start_at);
      const brkEnd = parseTimeField(avail.break_end_at);
      if (brkStart !== null && brkEnd !== null) {
        blocks.push({ start: brkStart, end: brkEnd, label: t("bms_cal_unavailable") });
      }
    }
    if (!matched) blocks.push({ start: startHour, end: endHour, label: "" });
    return blocks;
  };

  const totalBookings = useMemo(() => {
    return visibleResources.reduce((sum, r) => sum + r.events.length, 0);
  }, [visibleResources]);

  // Dynamic hour range that covers all visible experts' availability
  const visibleHours = useMemo(() => {
    const weekdays: number[] =
      viewMode === "week"
        ? Array.from({ length: 7 }, (_, i) => (getWeekStart(currentDate).getDay() + i) % 7)
        : [currentDate.getDay()];
    return computeVisibleHours(visibleResources, weekdays);
  }, [visibleResources, currentDate, viewMode]);

  const hourList = useMemo(
    () => buildHourList(visibleHours.startHour, visibleHours.endHour),
    [visibleHours]
  );

  return (
    <div className="chatbots-page bms-page">
      <div className="page-header-card mb-3">
        <div className="page-header-text">
          <h1 className="d-flex align-items-center gap-2">
            <TbCalendar /> {t("bms_calendar")}
          </h1>
          <p>{t("bms_calendar_desc")}</p>
        </div>
      </div>

      <div className="p-3 pt-0">
        <BmsChatbotSelector chatbotId={chatbotId} onChatbotChange={setChatbotId} />

        {chatbotId && (
          <>
            <div className="bms-cal-top-header mt-3">
              <div className="bms-cal-date-nav">
                <button className="nav-arrow" onClick={() => navDate(-1)}><TbChevronLeft /></button>
                <button className="today-btn" onClick={goToday}>{t("bms_cal_today")}</button>
                <div className="current-date">
                  {viewMode === "week"
                    ? `${toIsoDate(getWeekStart(currentDate)).slice(5)} – ${toIsoDate(addDays(getWeekStart(currentDate), 6)).slice(5)}`
                    : formatDate(currentDate)}
                </div>
                <button className="nav-arrow" onClick={() => navDate(1)}><TbChevronRight /></button>
              </div>
              <div className="bms-cal-view-switcher">
                {(["day", "week", "timeline"] as ViewMode[]).map((vm) => (
                  <button key={vm} className={`view-btn ${viewMode === vm ? "active" : ""}`} onClick={() => setViewMode(vm)}>
                    {vm === "day" ? <TbSun /> : vm === "week" ? <TbCalendarEvent /> : <TbTimeline />}
                    {" "}{t(`bms_cal_${vm}`)}
                  </button>
                ))}
              </div>
            </div>

            {expertInfos.length > 0 && (
              <div className="bms-cal-expert-bar">
                <span className="expert-bar-label">{t("bms_cal_experts_label")}:</span>
                {expertInfos.map((e) => {
                  const active = activeExperts.has(e.id);
                  return (
                    <div key={e.id} className={`expert-chip ${active ? "active" : "inactive"}`}
                      style={{ color: e.color, background: active ? hexToRgba(e.color, 0.08) : undefined }}
                      onClick={() => toggleExpert(e.id)}
                    >
                      <div className="chip-dot" style={{ background: e.color }}>{e.initials}</div>
                      {e.name}
                    </div>
                  );
                })}
              </div>
            )}

            {isLoading ? (
              <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>
            ) : visibleResources.length === 0 ? (
              <div className="bms-section-card"><div className="bms-empty"><div className="bms-empty-icon"><TbCalendar /></div><p>{t("bms_no_data")}</p></div></div>
            ) : (
              <>
                {viewMode === "day" && (
                  <DayView resources={visibleResources} experts={visibleExperts} currentDate={currentDate} today={today}
                    getUnavailableBlocks={getUnavailableBlocks} onEventClick={openModal} onSlotClick={handleSlotClick} t={t} totalBookings={totalBookings}
                    hourList={hourList} startHour={visibleHours.startHour} endHour={visibleHours.endHour} />
                )}
                {viewMode === "week" && (
                  <WeekView resources={visibleResources} experts={visibleExperts} currentDate={currentDate} today={today}
                    onEventClick={openModal} onSlotClick={handleSlotClick} t={t} totalBookings={totalBookings} />
                )}
                {viewMode === "timeline" && (
                  <TimelineView resources={visibleResources} experts={visibleExperts} currentDate={currentDate} today={today}
                    getUnavailableBlocks={getUnavailableBlocks} onEventClick={openModal} onSlotClick={handleSlotClick} t={t} totalBookings={totalBookings}
                    hourList={hourList} startHour={visibleHours.startHour} endHour={visibleHours.endHour} />
                )}
              </>
            )}
          </>
        )}
      </div>

      <BookingDetailModal data={modalData} onClose={() => setModalData(null)} chatbotId={chatbotId} onUpdated={handleBookingCreated} t={t} />
      <CreateBookingFromCalendarModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        chatbotId={chatbotId}
        context={createContext}
        onCreated={handleBookingCreated}
      />
    </div>
  );
};

/* ═══════════════════ DAY VIEW ═══════════════════ */

interface DayViewProps {
  resources: BmsCalendarResource[];
  experts: ExpertInfo[];
  currentDate: Date;
  today: Date;
  getUnavailableBlocks: (r: BmsCalendarResource, d: Date, sh: number, eh: number) => Array<{ start: number; end: number; label: string }>;
  onEventClick: (ev: BmsCalendarEvent, name: string, color: string) => void;
  onSlotClick: (date: Date, hour: number | undefined, expertId: number | undefined) => void;
  t: (k: string, opts?: any) => string;
  totalBookings: number;
  hourList: number[];
  startHour: number;
  endHour: number;
}

const DayView: React.FC<DayViewProps> = ({ resources, experts, currentDate, today, getUnavailableBlocks, onEventClick, onSlotClick, t, totalBookings, hourList, startHour, endHour }) => {
  const now = new Date();
  const isToday = isSameDay(currentDate, today);
  const nowHour = now.getHours() + now.getMinutes() / 60;

  return (
    <div className="bms-cal-container">
      <div className="bms-cal-day-grid" style={{ gridTemplateColumns: `70px repeat(${experts.length}, 1fr)` }}>
        <div className="time-gutter">
          <div className="time-gutter-header">{t("bms_cal_hour")}</div>
          {hourList.map((h) => (
            <div key={h} className="time-label">{`${String(h).padStart(2, "0")}:00`}</div>
          ))}
        </div>

        {resources.map((resource, idx) => {
          const info = experts[idx];
          if (!info) return null;
          const color = info.color;
          const events = getEventsForDate(resource.events, currentDate);
          const unavail = getUnavailableBlocks(resource, currentDate, startHour, endHour);

          return (
            <div key={info.id} className="expert-column">
              <div className="expert-col-header">
                <div className="avatar" style={{ background: color }}>{info.initials}</div>
                <div className="name">{info.name}</div>
              </div>
              <div style={{ position: "relative" }}>
                {hourList.map((h) => (
                  <div key={h} className="hour-cell cal-clickable" onClick={() => onSlotClick(currentDate, h, info.id)} title={t("bms_cal_click_to_book")}>
                    <div className="half-line" />
                    <div className="hour-cell-plus"><TbPlus size={12} /></div>
                  </div>
                ))}

                {unavail.map((u, i) => (
                  <div key={`u-${i}`} className="unavailable-block" style={{ top: (u.start - startHour) * PX_PER_HOUR, height: (u.end - u.start) * PX_PER_HOUR }}>
                    {u.label}
                  </div>
                ))}

                {events.map((ev) => {
                  const startH = parseHour(ev.start_at);
                  const endH = parseHour(ev.end_at);
                  const top = (startH - startHour) * PX_PER_HOUR;
                  const height = Math.max((endH - startH) * PX_PER_HOUR, 32);
                  const bg = hexToRgba(color, 0.14);
                  const si = statusInfo(ev.status, t);

                  return (
                    <div
                      key={ev.id}
                      className={`event-card ${ev.status}`}
                      style={{ top, height, background: bg, borderLeftColor: color, color }}
                      onClick={() => onEventClick(ev, info.name, color)}
                    >
                      <div className={`ev-status-dot ${ev.status}`} />
                      <div className="ev-time">
                        {extractTime(ev.start_at || "")} – {extractTime(ev.end_at || "")}
                      </div>
                      <div className="ev-title">{ev.service_name}</div>
                      {height >= 50 && ev.customer_name && (
                        <div className="ev-client">{ev.customer_name}</div>
                      )}
                      {/* Hover inline detail */}
                      <div className="ev-hover-detail">
                        <div className="ehd-row"><TbUser className="ehd-ico" /> {ev.customer_name || "-"}</div>
                        <div className="ehd-row"><TbBriefcase className="ehd-ico" /> {ev.service_name || "-"}</div>
                        <div className="ehd-row"><TbClock className="ehd-ico" /> {extractTime(ev.start_at || "")} – {extractTime(ev.end_at || "")}</div>
                        {ev.customer_phone && <div className="ehd-row"><TbPhone className="ehd-ico" /> {ev.customer_phone}</div>}
                        <div className={`ehd-badge ${si.cls}`}><span className="bms-status-dot" /> {si.label}</div>
                      </div>
                    </div>
                  );
                })}

                {isToday && nowHour >= startHour && nowHour < endHour && (
                  <div className="now-line" style={{ top: (nowHour - startHour) * PX_PER_HOUR }}>
                    <span className="now-line-label">{String(now.getHours()).padStart(2, "0")}:{String(now.getMinutes()).padStart(2, "0")}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <CalendarBottomBar t={t} totalBookings={totalBookings} />
    </div>
  );
};

/* ═══════════════════ WEEK VIEW ═══════════════════ */

interface WeekViewProps {
  resources: BmsCalendarResource[];
  experts: ExpertInfo[];
  currentDate: Date;
  today: Date;
  onEventClick: (ev: BmsCalendarEvent, name: string, color: string) => void;
  onSlotClick: (date: Date, hour: number | undefined, expertId: number | undefined) => void;
  t: (k: string, opts?: any) => string;
  totalBookings: number;
}

const WeekView: React.FC<WeekViewProps> = ({ resources, experts, currentDate, today, onEventClick, onSlotClick, t, totalBookings }) => {
  const weekStart = getWeekStart(currentDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  return (
    <div className="bms-cal-container">
      <div className="bms-cal-week-grid" style={{ gridTemplateColumns: `140px repeat(7, 1fr)` }}>
        <div className="week-header-cell" style={{ borderRight: "1px solid var(--border-color)" }}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600 }}>
            {t("bms_cal_expert").toUpperCase()}
          </div>
        </div>
        {weekDays.map((day, i) => {
          const td = isSameDay(day, today);
          return (
            <div key={i} className={`week-header-cell ${td ? "today-col" : ""}`}>
              <div className="day-name">{DAY_NAMES_SHORT[day.getDay()]}</div>
              <div className="day-num">
                {td ? <span className="day-num-today">{day.getDate()}</span> : day.getDate()}
              </div>
            </div>
          );
        })}

        {resources.map((resource, idx) => {
          const info = experts[idx];
          if (!info) return null;
          const color = info.color;

          return (
            <React.Fragment key={info.id}>
              <div className="week-resource-label">
                <div className="avatar" style={{ background: color }}>{info.initials}</div>
                <div><div className="name">{info.name}</div></div>
              </div>
              {weekDays.map((day, di) => {
                const td = isSameDay(day, today);
                const events = getEventsForDate(resource.events, day);
                const show = events.slice(0, 3);
                const more = events.length - 3;
                return (
                  <div key={di} className={`week-cell cal-clickable ${td ? "today-col" : ""}`} onClick={() => onSlotClick(day, undefined, info.id)} title={t("bms_cal_click_to_book")}>
                    {show.map((ev) => {
                      const si = statusInfo(ev.status, t);
                      return (
                        <div key={ev.id} className={`week-event ${ev.status}`}
                          style={{ background: hexToRgba(color, 0.1), borderLeftColor: color, color }}
                          onClick={(e) => { e.stopPropagation(); onEventClick(ev, info.name, color); }}
                        >
                          <div className="we-time">{extractTime(ev.start_at || "")}</div>
                          <div className="we-title">{ev.service_name}</div>
                          <div className={`we-status-dot ${ev.status}`} />
                        </div>
                      );
                    })}
                    {more > 0 && <div className="week-more">{t("bms_cal_more", { count: more })}</div>}
                    <div className="week-cell-plus"><TbPlus size={12} /></div>
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
      <CalendarBottomBar t={t} totalBookings={totalBookings} />
    </div>
  );
};

/* ═══════════════════ TIMELINE VIEW ═══════════════════ */

interface TimelineViewProps {
  resources: BmsCalendarResource[];
  experts: ExpertInfo[];
  currentDate: Date;
  today: Date;
  getUnavailableBlocks: (r: BmsCalendarResource, d: Date, sh: number, eh: number) => Array<{ start: number; end: number; label: string }>;
  onEventClick: (ev: BmsCalendarEvent, name: string, color: string) => void;
  onSlotClick: (date: Date, hour: number | undefined, expertId: number | undefined) => void;
  t: (k: string, opts?: any) => string;
  totalBookings: number;
  hourList: number[];
  startHour: number;
  endHour: number;
}

const TimelineView: React.FC<TimelineViewProps> = ({ resources, experts, currentDate, today, getUnavailableBlocks, onEventClick, onSlotClick, t, totalBookings, hourList, startHour, endHour }) => {
  const totalSlots = Math.max(1, endHour - startHour);
  const isToday = isSameDay(currentDate, today);
  const now = new Date();
  const nowHour = now.getHours() + now.getMinutes() / 60;

  return (
    <div className="bms-cal-container">
      <div className="bms-cal-timeline">
        <div className="timeline-header" style={{ gridTemplateColumns: "140px 1fr" }}>
          <div className="timeline-header-label">{t("bms_cal_experts_label")}</div>
          <div className="timeline-hours" style={{ gridTemplateColumns: `repeat(${hourList.length}, 1fr)` }}>
            {hourList.map((h) => (
              <div key={h} className="timeline-hour-label">{`${String(h).padStart(2, "0")}:00`}</div>
            ))}
          </div>
        </div>

        {resources.map((resource, idx) => {
          const info = experts[idx];
          if (!info) return null;
          const color = info.color;
          const events = getEventsForDate(resource.events, currentDate);
          const unavail = getUnavailableBlocks(resource, currentDate, startHour, endHour);

          return (
            <div key={info.id} className="timeline-row" style={{ gridTemplateColumns: "140px 1fr" }}>
              <div className="timeline-resource">
                <div className="avatar" style={{ background: color }}>{info.initials}</div>
                <div><div className="name">{info.name}</div></div>
              </div>
              <div className="timeline-slots" style={{ gridTemplateColumns: `repeat(${hourList.length}, 1fr)` }}>
                {hourList.map((h) => <div key={h} className="timeline-slot-bg cal-clickable" onClick={() => onSlotClick(currentDate, h, info.id)} title={t("bms_cal_click_to_book")} />)}

                {unavail.map((u, i) => (
                  <div key={`u-${i}`} className="timeline-unavailable" style={{ left: `${((u.start - startHour) / totalSlots) * 100}%`, width: `${((u.end - u.start) / totalSlots) * 100}%` }} />
                ))}

                {events.map((ev) => {
                  const startH = parseHour(ev.start_at);
                  const endH = parseHour(ev.end_at);
                  const left = ((startH - startHour) / totalSlots) * 100;
                  const width = ((endH - startH) / totalSlots) * 100;
                  return (
                    <div key={ev.id} className={`timeline-event ${ev.status}`}
                      style={{ left: `${left}%`, width: `${width}%`, background: hexToRgba(color, 0.15), borderLeftColor: color, color }}
                      onClick={() => onEventClick(ev, info.name, color)}
                    >
                      <div className={`te-dot ${ev.status}`} />
                      <span className="te-label">{ev.service_name} · {ev.customer_name || "-"}</span>
                    </div>
                  );
                })}

                {isToday && nowHour >= startHour && nowHour < endHour && (
                  <div className="timeline-now" style={{ left: `${((nowHour - startHour) / totalSlots) * 100}%` }} />
                )}
              </div>
            </div>
          );
        })}
      </div>
      <CalendarBottomBar t={t} totalBookings={totalBookings} />
    </div>
  );
};

/* ═══════════════════ BOTTOM BAR ═══════════════════ */

const CalendarBottomBar: React.FC<{ t: (k: string) => string; totalBookings: number }> = ({ t, totalBookings }) => (
  <div className="bms-cal-bottom-bar">
    <div className="legend-row">
      <div className="legend-item"><div className="legend-dot" style={{ background: "#22c55e" }} /> {t("bms_cal_confirmed")}</div>
      <div className="legend-item"><div className="legend-dot" style={{ background: "#f59e0b" }} /> {t("bms_cal_pending")}</div>
      <div className="legend-item"><div className="legend-dot" style={{ background: "#ef4444" }} /> {t("bms_cal_cancelled")}</div>
      <div className="legend-item"><div className="legend-stripe" /> {t("bms_cal_legend_unavailable")}</div>
    </div>
    <div className="summary-text">
      <strong>{totalBookings}</strong> {t("bms_bookings").toLowerCase()}
    </div>
  </div>
);

export default BmsCalendarPage;
